// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getUserProfile, login as loginAPI, logout as logoutAPI, getDevices, updateUserProfile } from '../utils/api';
import toast from '../utils/toastUtils';
import socketClient from '../utils/socketClient';
import { tokenStorage, secureStorage } from '../utils/secureStorage';

// Create context
const AuthContext = createContext(null);

// Global logout handler for API interceptor (set by AuthProvider)
let globalLogoutHandler = null;

export const setGlobalLogoutHandler = (handler) => {
  globalLogoutHandler = handler;
};

export const getGlobalLogoutHandler = () => globalLogoutHandler;

const getRoleDefaultRoute = (role) => {
  switch (role) {
    case 'admin':
      return '/admin/admin';
    case 'moderator':
      return '/moderator';
    case 'agent':
      return '/agent';
    case 'user':
      return '/user/dashboard';
    default:
      return '/';
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isModerator, setIsModerator] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [devices, setDevices] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  // Socket event handler for notifications
  const handleNewNotification = useCallback(
    (notification) => {
      if (!isAuthenticated) return;
      const { type, message } = notification;
      switch (type) {
        case 'info':
          toast.info(message);
          break;
        case 'success':
          toast.success(message);
          break;
        case 'warning':
          toast.warning(message);
          break;
        case 'error':
          toast.error(message);
          break;
        default:
          toast.info(message);
      }
    },
    [isAuthenticated]
  );

  // Function to fetch user devices
  const fetchDevices = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const deviceData = await getDevices();
      setDevices(deviceData);
    } catch (err) {
      console.error('Failed to fetch devices:', err);
      if (isAuthenticated) {
        toast.warn('Unable to load device information');
      }
    }
  }, [isAuthenticated]);

  // Function to refresh user data
  const refreshUserData = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const userData = await getUserProfile();
      setUser(userData);
      setIsAdmin(userData.role === 'admin');
      setIsModerator(userData.role === 'moderator');
      await fetchDevices();
    } catch (err) {
      console.error('Failed to refresh user data:', err);
      if (isAuthenticated) {
        toast.error('Unable to refresh user information');
      }
    }
  }, [isAuthenticated, fetchDevices]);

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      const token = tokenStorage.getToken();
      if (!token) {
        setLoading(false);
        if (process.env.NODE_ENV === 'development') {
          console.log('Auth Debug - No token found, user not authenticated');
        }
        return;
      }
      if (location.pathname.includes('/login') || location.pathname.includes('/register')) {
        setLoading(false);
        if (process.env.NODE_ENV === 'development') {
          console.log('Auth Debug - On auth page, skipping initialization');
        }
        return;
      }
      try {
        if (process.env.NODE_ENV === 'development') {
          console.log('Auth Debug - Initializing auth with token');
        }
        const userData = await getUserProfile();
        setUser(userData);
        setIsAdmin(userData.role === 'admin');
        setIsModerator(userData.role === 'moderator');
        setIsAuthenticated(true);
        // Restore session activity tracking
        localStorage.setItem('lastUserActivity', Date.now().toString());
        if (process.env.NODE_ENV === 'development') {
          console.log('Auth Debug - User authenticated:', userData.role);
        }
        if (userData) {
          socketClient.setupEventListeners(handleNewNotification);
          await socketClient.connect();
          await fetchDevices();
        }
      } catch (err) {
        console.error('Authentication initialization failed:', err);
        if (process.env.NODE_ENV === 'development') {
          console.log('Auth Debug - Auth initialization failed, clearing token');
        }
        tokenStorage.removeToken();
        setUser(null);
        setIsAdmin(false);
        setIsModerator(false);
        setIsAuthenticated(false);
        setDevices([]);
        // Silent redirect to login for token expiration/invalid
        navigate('/login', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    return () => {
      if (isAuthenticated) {
        socketClient.removeEventListeners();
        socketClient.disconnect();
      }
    };
  }, [location.pathname, handleNewNotification, fetchDevices, navigate]); // Removed isAuthenticated from dependencies

  // Login function
  const login = async (credentials) => {
    try {
      setLoading(true);
      setError(null);
      const { emailOrName, password } = credentials;
      const response = await loginAPI(emailOrName, password);
      const { account: loggedInUser, token, loggedOutDevices } = response;
      if (token) {
        tokenStorage.setToken(token);
      }
      // Initialize session activity tracking
      localStorage.setItem('lastUserActivity', Date.now().toString());
      setUser(loggedInUser);
      setIsAdmin(loggedInUser.role === 'admin');
      setIsModerator(loggedInUser.role === 'moderator');
      setIsAuthenticated(true);
      console.log('🔍 Login Debug:', {
        userRole: loggedInUser.role,
        isModerator: loggedInUser.role === 'moderator',
        userData: loggedInUser
      });
      // Device logout notifications removed
      socketClient.setupEventListeners(handleNewNotification);
      await socketClient.connect();
      await fetchDevices();
      // toast.success('Logged in successfully!'); // Removed login success notification
      const redirectPath = getRoleDefaultRoute(loggedInUser.role);
      navigate(redirectPath, { replace: true });
      return true;
    } catch (err) {
      console.error('Login failed:', err);
      const errorMessage = err.response?.data?.message || 'Login failed. Please try again.';
      setError(errorMessage);
      // Let the caller handle error messages to avoid duplicate toasts
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Logout function with optional silent mode (no toast notifications)
  const logout = useCallback(async (silent = false) => {
    try {
      setLoading(true);
      if (isAuthenticated) {
        try {
          const logoutResponse = await logoutAPI();
          console.log('Logout API call successful:', logoutResponse);
        } catch (err) {
          // Silently handle logout API errors (e.g., token already expired)
          console.log('Logout API error (ignored):', err);
        }
        socketClient.removeEventListeners();
        socketClient.disconnect();
      }
      tokenStorage.removeToken();
      // Clean up session management data
      localStorage.removeItem('lastUserActivity');
      localStorage.removeItem('pageHiddenTime');
      localStorage.removeItem('pageUnloadTime');
      // Clear cached subscription data
      secureStorage.removeItem('user_subscription');
      setUser(null);
      setIsAdmin(false);
      setIsModerator(false);
      setIsAuthenticated(false);
      setDevices([]);
      if (!silent) {
        toast.success('Logged out successfully');
      }
      navigate('/login', { replace: true });
      return true;
    } catch (err) {
      console.error('Logout failed:', err);
      const errorMessage = err.response?.data?.message || 'Logout failed. Please try again.';
      setError(errorMessage);
      if (!silent) {
        toast.error(errorMessage);
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, navigate]);

  // Update profile function
  const updateProfile = async (profileData) => {
    if (!isAuthenticated) {
      toast.error('You must be logged in to update your profile');
      return null;
    }
    try {
      setLoading(true);
      const updatedUser = await updateUserProfile(profileData);
      setUser(updatedUser);
      setIsAdmin(updatedUser.role === 'admin');
      setIsModerator(updatedUser.role === 'moderator');
      toast.success('Profile updated successfully');
      return updatedUser;
    } catch (err) {
      console.error('Profile update failed:', err);
      const errorMessage = err.response?.data?.message || 'Failed to update profile';
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Clear error function
  const clearError = () => setError(null);

  // Set global logout handler for API interceptor
  useEffect(() => {
    setGlobalLogoutHandler(() => logout(true)); // Silent logout for token expiration
    return () => {
      setGlobalLogoutHandler(null);
    };
  }, [logout]);

  // Auth context value
  const value = {
    user,
    isAdmin,
    isModerator,
    isAuthenticated,
    loading,
    error,
    devices,
    login,
    logout,
    updateProfile,
    refreshUserData,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Updated ProtectedRoute with allowedRoles and updated redirection for users
export const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading) {
      if (!isAuthenticated) {
        navigate('/login', {
          state: { from: location.pathname },
          replace: true,
        });
      } else if (user?.role && !allowedRoles.includes(user.role)) {
        const redirectTo = getRoleDefaultRoute(user?.role);
        console.log('🔍 ProtectedRoute Debug:', {
          userRole: user?.role,
          allowedRoles,
          currentPath: location.pathname,
          redirectTo
        });
        navigate(redirectTo, { replace: true });
      }
    }
  }, [loading, isAuthenticated, user, allowedRoles, navigate, location]);

  if (loading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  return isAuthenticated ? children : null;
};

export default AuthProvider;
